import React from "react";
import { Modal, Button, Typography, Row, Col, Card, Divider, Badge } from "antd";
import { Link } from "react-router-dom";
import { ExternalLink, Clock, Bell, Edit, CheckCircle, FileText, Search, Folder, XCircle, Calendar } from "lucide-react";
import moment from "moment";
import { formatFullControlInterno, formatFullDate, getTypeFullName } from "./calendarUtils";

const { Title, Text } = Typography;

const DayDetailsModal = ({ visible, onCancel, day, items, zIndex }) => {
  const renderVisitas = () => {
    const visitas = items.filter((item) => item.Tipo === "visita");
    if (visitas.length === 0) return null;

    return (
      <>
        <Title level={4} style={{ marginBottom: "16px", color: "#1890ff", borderBottom: "2px solid #1890ff", paddingBottom: "8px" }}>
          <Clock size={18} style={{ marginRight: "8px" }} />
          Visitas Programadas ({visitas.length})
        </Title>
        {visitas
          .sort((a, b) => {
            if (!a.Hora1 || !b.Hora1) return 0;
            return moment(a.Hora1, "HH:mm").diff(moment(b.Hora1, "HH:mm"));
          })
          .map((item, index) => (
            <Card key={`visita-${index}`} size="small" style={{ marginBottom: "12px", borderLeft: "4px solid #1890ff" }} className={item.Status === 5 ? "cancelled-event" : ""}>
              <Row justify="space-between" align="middle">
                <Col span={16}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Text strong style={{ fontSize: "16px" }} className={item.Status === 5 ? "cancelled-ci" : ""}>
                      Control Interno: {formatFullControlInterno(item.ControlInterno)}
                    </Text>
                    <Text style={{ fontSize: "14px" }}>Verificador: {item.Verificador}</Text>
                    <Text style={{ fontSize: "14px" }}>Status: {item.Status}</Text>
                    <Text style={{ fontSize: "14px" }}>Monto: {item.Monto}</Text>
                    {item.Hora1 && item.Hora2 && (
                      <Text style={{ fontSize: "14px", color: "#666" }}>
                        <Clock size={14} style={{ marginRight: "6px" }} />
                        {moment(item.Hora1, "HH:mm").format("HH:mm")} - {moment(item.Hora2, "HH:mm").format("HH:mm")}
                        <span style={{ marginLeft: "12px", color: "#999" }}>
                          ({moment(item.Hora2, "HH:mm").diff(moment(item.Hora1, "HH:mm"), "minutes")} min)
                        </span>
                      </Text>
                    )}
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  <Link to={`/verificaciones/${item.id}`}>
                    <Button type="primary" size="small" icon={<ExternalLink size={14} />}>
                      Ver Verificación
                    </Button>
                  </Link>
                </Col>
              </Row>
            </Card>
          ))}
        <Divider />
      </>
    );
  };

  const renderOtherActivities = () => {
    const otherItems = items.filter((item) => item.Tipo !== "visita");
    if (otherItems.length === 0) return null;

    return (
      <>
        <Title level={4} style={{ marginBottom: "16px", color: "#52c41a", borderBottom: "2px solid #52c41a", paddingBottom: "8px" }}>
          <FileText size={18} style={{ marginRight: "8px" }} />
          Otras Actividades ({otherItems.length})
        </Title>
        <Row gutter={[16, 12]}>
          {otherItems.map((item, index) => (
            <Col span={12} key={`other-${index}`}>
              <Card
                size="small"
                className={item.Status === 5 ? "cancelled-event" : ""}
                style={{
                  height: "100%",
                  borderLeft: `4px solid ${
                    item.Tipo === "solicitud"
                      ? "#52c41a"
                      : item.Tipo === "contrato"
                      ? "#722ed1"
                      : item.Tipo === "listo"
                      ? "#13c2c2"
                      : item.Tipo === "emision"
                      ? "#faad14"
                      : item.Tipo === "revision1"
                      ? "#13c2c2"
                      : item.Tipo === "revision2"
                      ? "#fa8c16"
                      : item.Tipo === "papeleria"
                      ? "#a0d911"
                      : "#f5222d"
                  }`,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {item.Tipo === "solicitud" && <Bell size={16} />}
                    {item.Tipo === "contrato" && <Edit size={16} />}
                    {item.Tipo === "listo" && <CheckCircle size={16} />}
                    {item.Tipo === "emision" && <FileText size={16} />}
                    {(item.Tipo === "revision1" || item.Tipo === "revision2") && <Search size={16} />}
                    {item.Tipo === "papeleria" && <Folder size={16} />}
                    {item.Tipo === "cancelacion" && <XCircle size={16} />}
                    <Text strong style={{ fontSize: "14px" }}>
                      {getTypeFullName(item.Tipo)}
                    </Text>
                  </div>
                  <Text style={{ fontSize: "16px", fontWeight: 500 }} className={item.Status === 5 ? "cancelled-ci" : ""}>CI: {formatFullControlInterno(item.ControlInterno)}</Text>
                  <div style={{ marginTop: "auto" }}>
                    <Link to={`/verificaciones/${item.id}`}>
                      <Button type="link" size="small" icon={<ExternalLink size={12} />} style={{ padding: 0 }}>
                        Ver Verificación
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Calendar size={20} />
          <span style={{ fontSize: "18px", fontWeight: 600 }}>Actividades del {day ? formatFullDate(day.format("YYYY-MM-DD")) : ""}</span>
          <Badge count={items?.length || 0} style={{ backgroundColor: "#1890ff" }} />
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      zIndex={zIndex}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cerrar
        </Button>,
      ]}
      width={900}
    >
      {items && items.length > 0 ? (
        <div style={{ padding: "20px 0" }}>
          {renderVisitas()}
          {renderOtherActivities()}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px", color: "#d9d9d9" }}>📅</div>
          <Title level={4} type="secondary">
            No hay actividades programadas
          </Title>
          <Text type="secondary">Este día no tiene actividades registradas</Text>
        </div>
      )}
    </Modal>
  );
};

export default DayDetailsModal;
